import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';

import api from '../../../services/api';

import { useToast } from '../../../hooks/toast';
import getValidationErrors from '../../../utils/getValidationErrors';

import FormSurveyText from '../Form';

interface Translation {
  text: string;
  languageId: number;
}

interface SurveyText {
  title: string;
  text: string;
  isStartText: boolean;
  defaultLanguageId?: number;
  translations: Translation[];
  textTranslated?: string;
  translationLanguageId?: number;
}

interface ParamTypes {
  id: string;
}

const EditSurveyText: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();
  const { id } = useParams();
  const [surveyText, setSurveyText] = useState<SurveyText>();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/surveyTexts/${id}`).then(response => {
      setSurveyText(response.data);
      formRef.current?.setFieldValue('defaultLanguageId', {
        value: response.data.defaultLanguageId,
        label: response.data.defaultLanguageName,
      });
    });
  }, [id]);

  const handleSubmit = useCallback(
    async (data: SurveyText) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          title: Yup.string().required('The title is required'),
          text: Yup.string().required('The text required'),
          defaultLanguageId: Yup.number().required('The text required'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const surveyTextOriginal = surveyText;
        const translationArrayOriginal =
          surveyTextOriginal?.translations?.filter(
            (x: any) =>
              x.languageId !== data.translationLanguageId &&
              x.languageId !== data.defaultLanguageId,
          );

        const translationsArray = translationArrayOriginal ?? [];

        if (
          data &&
          data.defaultLanguageId &&
          data.translationLanguageId &&
          data.defaultLanguageId !== data.translationLanguageId
        ) {
          translationsArray?.push({
            text: data.textTranslated ?? '',
            languageId: data.translationLanguageId ?? 0,
          });
        }
        const surveyTextToEdit = {
          text: data.text,
          title: data.title,
          defaultLanguageId: data.defaultLanguageId,
          translations: translationsArray,
        };
        await api.put(`/surveyTexts/${id}`, surveyTextToEdit);

        const redirectUrl =
          surveyText?.isStartText === true
            ? '/surveyTexts/type/start'
            : '/surveyTexts/type/final';

        navigate(redirectUrl);

        addToast({
          type: 'success',
          title: 'Success',
          description: 'The text was saved successfully!',
        });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          return;
        }

        addToast({
          type: 'error',
          title: 'Registration Error',
          description:
            'An error occurred while editing the text, please try again.',
        });
      }
    },
    [surveyText, id, navigate, addToast],
  );

  return (
    <>
      <h1>Edit {surveyText?.isStartText === true ? 'Start' : 'Final'} Text</h1>

      <FormSurveyText
        formRef={formRef}
        handleSubmit={handleSubmit}
        initialData={surveyText}
        buttonText="Save"
      />
    </>
  );
};

export default EditSurveyText;
